/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { ErrorTypes, LiveTvError } from '../../error';
import { Locale } from '../../locale/Locale';
import { LocaleKeys } from '../../locale/LocaleKeys';
import { Log } from '../../logging';
import { ShakaPlayerUtils } from '../utils';

/*
    Shaka error and other playback related error Example: vp config fail, airing fail, ulr missing 
    or grace period ended, All this error mapped as part of LiveTvPlaybackErrorMapper.
*/

// using release build
const shaka = require('shaka-player/shaka-player.compiled');

export class LiveTvPlaybackErrorMapper {
    private static TAG: string = 'LiveTvPlaybackErrorMapper';

    private code: string;

    private description: string;

    private message: string;

    private subErrorCode: string;

    private title: string;

    private type: string;

    public constructor() {
        this.code = '';
        this.description = '';
        this.message = '';
        this.subErrorCode = '';
        this.title = '';
        this.type = '';
    }

    private mapPlaybackErrorProperties(error) {
        switch (error.category) {
            case shaka.util.Error.Category.ADS:
                this.code = ErrorTypes.LIVE_TV_SHAKA_PLAYER_ADS_ERROR_V960;
                this.type = 'PLAYER_ADS_ERROR';
                this.message = 'Errors related to ad insertion';
                break;
            case shaka.util.Error.Category.CAST:
                this.code = ErrorTypes.LIVE_TV_SHAKA_PLAYER_CAST_ERROR_V958;
                this.type = 'PLAYER_CAST_ERROR';
                this.message = 'Errors related to cast.';
                break;
            case shaka.util.Error.Category.DRM:
                this.code = ErrorTypes.LIVE_TV_SHAKA_PLAYER_DRM_ERROR_V956;
                this.type = 'PLAYER_DRM_ERROR';
                this.message = 'Errors related to DRM.';
                if (ShakaPlayerUtils.getHttpErrorCode(error)) {
                    this.code = ErrorTypes.LIVE_TV_SHAKA_PLAYER_ENFORCEMENT_ERROR_V948;
                }
                break;
            case shaka.util.Error.Category.MANIFEST:
                this.code = ErrorTypes.LIVE_TV_SHAKA_PLAYER_MANIFEST_ERROR_V954;
                this.type = 'PLAYER_MANIFEST_ERROR';
                this.message = 'Errors parsing the Manifest.';
                break;
            case shaka.util.Error.Category.MEDIA:
                this.code = ErrorTypes.LIVE_TV_SHAKA_PLAYER_MEDIA_ERROR_V953;
                this.type = 'PLAYER_MEDIA_ERROR';
                this.message = 'Errors parsing or processing audio or video streams.';
                break;
            case shaka.util.Error.Category.NETWORK:
                this.code = ErrorTypes.LIVE_TV_SHAKA_PLAYER_NETWORK_ERROR_V951;
                this.type = 'PLAYER_NETWORK_ERROR';
                this.message = 'Errors from the network stack.';
                break;
            case shaka.util.Error.Category.PLAYER:
                this.code = ErrorTypes.LIVE_TV_SHAKA_PLAYER_PLAYER_ERROR_V957;
                this.type = 'PLAYER_MISCELLANEOUS_ERROR';
                this.message = 'Miscellaneous errors from the player.';
                break;
            case shaka.util.Error.Category.STORAGE:
                this.code = ErrorTypes.LIVE_TV_SHAKA_PLAYER_STORAGE_ERROR_V959;
                this.type = 'PLAYER_STORAGE_ERROR';
                this.message = 'Errors in the database storage (offline).';
                break;
            case shaka.util.Error.Category.STREAMING:
                this.code = ErrorTypes.LIVE_TV_SHAKA_PLAYER_STREAMING_MEDIA_V955;
                this.type = 'PLAYER_STREAMING_ERROR';
                this.message = 'Errors related to streaming';
                break;
            case shaka.util.Error.Category.TEXT:
                this.code = ErrorTypes.LIVE_TV_SHAKA_PLAYER_TEXT_ERROR_V952;
                this.type = 'PLAYER_TEXT_ERROR';
                this.message = 'Errors parsing text streams.';
                break;
            default:
                break;
        }
    }

    private setPlaybackErrorPropertiesByCode(
        errorCode: string,
        subErrorCode?: string,
        contentType?: string,
    ) {
        Log.e(LiveTvPlaybackErrorMapper.TAG, 'playback error code ', errorCode);
        Diagnostics.error(LiveTvPlaybackErrorMapper.TAG, 'playback error code ', errorCode);
        const errorSeparator = LiveTvError.separator;
        switch (errorCode) {
            case ErrorTypes.LIVE_TV_AIRING_DATA_FAILED_V139:
                this.code = errorCode;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_MESSAGE);
                break;
            case ErrorTypes.LIVE_TV_DEVICE_ERROR_MISSING_CONFIG_V904:
                this.code = errorCode;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_DEVICE_CONFIG_ERROR_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_DEVICE_CONFIG_ERROR_MESSAGE);
                break;
            case ErrorTypes.LIVE_TV_GRACE_PERIOD_ENDED_ERROR_C718:
                this.code = errorCode;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_GRACE_PERIOD_ENDED_ERROR_TITLE);
                this.description = Locale.getString(
                    LocaleKeys.LIVE_TV_GRACE_PERIOD_ENDED_ERROR_MESSAGE,
                );
                break;
            case ErrorTypes.LIVE_TV_UNSUPPORTED_CONTENT_ERROR_V950:
                this.code = `${errorCode}${errorSeparator}${subErrorCode}`;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.description = Locale.getString(
                    LocaleKeys.LIVE_TV_UNSUPPORTED_CONTENT_ERROR_MESSAGE,
                ).replace('CONTENT_TYPE', contentType!);
                break;
            case ErrorTypes.LIVE_TV_NOT_READY_FOR_PLAYBACK_V117:
            case ErrorTypes.LIVE_TV_PLAYLIST_URI_MISSING_V908:
            case ErrorTypes.LIVE_TV_VPCONFIGURATIONS_EMPTY_RESPONSE_ERROR_V934:
            case ErrorTypes.LIVE_TV_VPCONFIGURATIONS_MISCONFIGURATION_ERROR_V909:
                this.code = errorCode;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_CANT_PLAY_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_UNABLE_TO_PLAY_MESSAGE);
                break;
            default:
                this.code = ErrorTypes.LIVE_TV_SHAKA_PLAYER_UNKNOWN_ERROR_V999;
                this.title = Locale.getString(LocaleKeys.LIVE_TV_PLAYER_ERROR_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_UNABLE_TO_PLAY_MESSAGE);
                break;
        }
    }

    private setPlaybackErrorPropertiesByError(error: any) {
        Log.e(LiveTvPlaybackErrorMapper.TAG, 'playback error ', error);
        Diagnostics.error(LiveTvPlaybackErrorMapper.TAG, 'playback error ', error);
        this.mapPlaybackErrorProperties(error);
        this.subErrorCode = ShakaPlayerUtils.getSubErrorCode(error);
        const errorSeparator = LiveTvError.separator;
        switch (this.code) {
            case ErrorTypes.LIVE_TV_SHAKA_PLAYER_ADS_ERROR_V960:
            case ErrorTypes.LIVE_TV_SHAKA_PLAYER_CAST_ERROR_V958:
            case ErrorTypes.LIVE_TV_SHAKA_PLAYER_DRM_ERROR_V956:
            case ErrorTypes.LIVE_TV_SHAKA_PLAYER_MANIFEST_ERROR_V954:
            case ErrorTypes.LIVE_TV_SHAKA_PLAYER_MEDIA_ERROR_V953:
            case ErrorTypes.LIVE_TV_SHAKA_PLAYER_NETWORK_ERROR_V951:
            case ErrorTypes.LIVE_TV_SHAKA_PLAYER_PLAYER_ERROR_V957:
            case ErrorTypes.LIVE_TV_SHAKA_PLAYER_STORAGE_ERROR_V959:
            case ErrorTypes.LIVE_TV_SHAKA_PLAYER_STREAMING_MEDIA_V955:
            case ErrorTypes.LIVE_TV_SHAKA_PLAYER_TEXT_ERROR_V952:
                this.title = Locale.getString(LocaleKeys.LIVE_TV_PLAYER_ERROR_TITLE);
                this.description = Locale.getString(LocaleKeys.LIVE_TV_UNABLE_TO_PLAY_MESSAGE);
                this.code = `${this.code}${errorSeparator}${this.subErrorCode}`;
                break;
            case ErrorTypes.LIVE_TV_SHAKA_PLAYER_ENFORCEMENT_ERROR_V948:
                this.title = Locale.getString(LocaleKeys.LIVE_TV_PLAYER_ENFORCEMENT_ERROR_TITLE);
                this.description = Locale.getString(
                    LocaleKeys.LIVE_TV_PLAYER_ENFORCEMENT_ERROR_MESSAGE,
                );
                this.code = `${this.code}${errorSeparator}${this.subErrorCode}`;
                break;
            default:
        }
    }

    public getErrorByCode(
        errorCode: string,
        subErrorCode?: string,
        contentType?: string,
    ): LiveTvError {
        this.setPlaybackErrorPropertiesByCode(errorCode, subErrorCode, contentType);
        return new LiveTvError(this.code, this.message, this.description, this.title, this.type);
    }

    public getErrorByErrorObject(error: any): LiveTvError {
        this.setPlaybackErrorPropertiesByError(error);
        return new LiveTvError(this.code, this.message, this.description, this.title, this.type);
    }
}
